import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { Target, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError("Ocurrió un error. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] flex flex-col items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
            <Target className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-slate-50 tracking-tight">RadarLit</h1>
          <p className="text-slate-400 text-sm mt-1">Tu radar en la contratación pública · SECOP II</p>
        </div>

        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          {sent ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-slate-200 font-semibold text-lg mb-3">Revisa tu correo</h2>
              <p className="text-slate-400 text-sm mb-6">
                Si el correo <span className="text-emerald-400">{email}</span> está registrado, recibirás un enlace para restablecer tu contraseña en los próximos minutos.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio de sesión
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-slate-200 font-semibold text-lg mb-2">Recuperar contraseña</h2>
              <p className="text-slate-400 text-sm mb-6">
                Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3 mb-5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-slate-400 text-xs font-medium mb-1.5 block">
                    Correo electrónico
                  </label>
                  <input
                    data-testid="forgot-password-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                    placeholder="tu@email.com"
                  />
                </div>

                <button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? "Enviando..." : "Enviar enlace de recuperación"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-400 text-xs transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Volver al inicio de sesión
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
